import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Typography } from '@material-ui/core';
import { TextField, Button } from '@lavu-pos/lavu-react-components';
import {
	StyledLink,
	Colors,
	HoverColors,
	intlShape,
	Spacer,
} from 'modules/utils';
import { Link } from 'react-router-dom';
import axios from 'axios';
import compose from 'lodash/fp/compose';
import { string as yupString, object as yupObject } from 'yup';
import { Formik } from 'formik';
import { FORGOT_PASSWORD_URL } from 'utils/constants';
import { withAuthContext } from './context';
import {
	StyledLinkWrapper,
	StyledFormMessage,
	StyledForm,
	StyledFormHeader,
	StyledInputWrapper,
} from './styles';
import { loadProtect } from './Protect';

const validationSchema = yupObject().shape({
	username: yupString().required(
		<FormattedMessage id="loginErrors.empty.username" />
	),
});

class PasswordResetForm extends Component {
	static propTypes = {
		updateUsername: PropTypes.func.isRequired,
		username: PropTypes.string.isRequired,
		history: PropTypes.shape({
			push: PropTypes.func,
		}),
		intl: intlShape,
	};

	state = {
		email: '',
		backEndError: '',
		usernameError: false,
		backEndSuccess: false,
		resetPassword: false,
	};

	onPageChange = () => {
		const { username } = this.state;
		const { updateUsername } = this.props;

		updateUsername(username);
	};

	returnToLogin = () => {
		this.props.history.push('/login');
	};

	onSubmit = async ({ username }, { setSubmitting }) => {
		const { updateUsername, intl, history } = this.props;

		try {
			const { data } = await axios.post(FORGOT_PASSWORD_URL, {
				username,
			}, { withCredentials: true });

			updateUsername(username);
			this.setState({
				backEndSuccess: true,
				usernameError: false,
				backEndError: '',
				email: data.user.email,
			});
		} catch (error) {
			const { response: { data = {} } = {} } = error;
			const { code = 'loginErrors.networkErrorMsg', usernameError } = data;

			if (error.response && error.response.headers['x-session']) {
				history.push('/refresh_page?code='+error.response.headers['x-session']);
			}

			this.setState({
				backEndError: intl.formatMessage({
					id: error.response ? `hubqlErrors.${code}` : code,
				}),
				usernameError,
			});

			setSubmitting(false);
		}
	};

	componentWillMount()
	{
	  loadProtect();
	}

	render() {
		const { backEndError, backEndSuccess, usernameError } = this.state;

		const passwordMessage = backEndSuccess ? (
			<FormattedMessage id="passwordResetCommon.successSubTitle" />
		) : (
			<FormattedMessage id="passwordResetCommon.defaultSubTitle" />
		);
		const { username: initialUsername } = this.props;

		return (
			<Formik
				initialValues={{ username: initialUsername }}
				isInitialValid={initialUsername?.length > 0}
				validationSchema={validationSchema}
				onSubmit={this.onSubmit}
			>
				{({
					values: { username },
					errors,
					handleSubmit,
					handleChange,
					isValid,
					isSubmitting,
				}) => (
					<StyledForm>
						<StyledFormHeader>
							<Typography className="password-reset-form-text password-reset-form-title">
								<FormattedMessage id="passwordResetCommon.resetPasswordTitle" />
							</Typography>
						</StyledFormHeader>
						<div>
							<Typography className="password-reset-form-text password-reset-message">
								{passwordMessage}
							</Typography>
						</div>
						{(!!backEndError || usernameError) && (
							<StyledFormMessage>
								<Typography className="password-reset-form-text password-reset-error">
									{usernameError ? (
										<Fragment>
											<FormattedMessage id="passwordResetEmail.errorUsername" />
											<FormattedMessage id="passwordResetEmail.customerCare" />.
										</Fragment>
									) : (
										backEndError
									)}
								</Typography>
							</StyledFormMessage>
						)}
						{backEndSuccess && (
							<Fragment>
								<StyledFormMessage
									bgcolor={Colors.SUCCESS}
									bdcolor={HoverColors.HOVER_GREEN}
								>
									<Typography className="password-reset-form-text password-reset-error">
										<FormattedMessage id="passwordResetEmail.successSentEmail" />
									</Typography>
								</StyledFormMessage>
								<Spacer height={5} />
								<Button kind="success" onClick={this.returnToLogin} fluid>
									<FormattedMessage id="passwordResetForm.returnLogin" />
								</Button>
							</Fragment>
						)}
						{!backEndSuccess && (
							<Fragment>
								<StyledInputWrapper>
									<TextField
										className="login-form-input"
										type="text"
										name="username"
										label={<FormattedMessage id="commonStrings.username" />}
										autoComplete="username"
										value={username}
										onChange={handleChange}
										error={errors.username}
										initialFocus
									/>
								</StyledInputWrapper>
								<Button
									kind="success"
									disabled={!isValid || isSubmitting}
									isLoading={isSubmitting}
									type="submit"
									fluid
									onClick={handleSubmit}
								>
									{isSubmitting ? (
										<FormattedMessage id="loading" />
									) : (
										<FormattedMessage id="passwordResetCommon.submitBtnMsg" />
									)}
								</Button>
								<StyledLinkWrapper>
									<StyledLink
										Component={Link}
										to="/login"
										onClick={this.onPageChange}
									>
										<Typography className="password-reset-form-text password-reset-link">
											<FormattedMessage id="passwordResetCommon.linkToLoginMsg" />
										</Typography>
									</StyledLink>
								</StyledLinkWrapper>
							</Fragment>
						)}
					</StyledForm>
				)}
			</Formik>
		);
	}
}

export default compose(
	injectIntl,
	withAuthContext(({ username, updateUsername }) => ({
		username,
		updateUsername,
	}))
)(PasswordResetForm);
